import React from 'react';
import {FormattedMessage, intlShape, injectIntl} from 'react-intl';
import branchApi from 'api/branch';
import Dealer from 'models/dealer';
import dealerApi from 'api/dealer';
import events from 'lib/events';
import FooterEditButton from 'components/layout/footerEditButton';
import hopscotch from 'hopscotch';
import ImageUpload from 'components/forms/LayoutImageUpload';
import LayoutCheckbox from 'components/forms/layoutCheckbox';
import LayoutInput from 'components/forms/layoutInput';
import LayoutSelect from 'components/forms/layoutSelect';
import PageFooter from 'components/layout/pageFooter';
import phoneFormat from 'helpers/phoneFormat';
import session from 'services/sessionService';
import ToolTip, {tooltipPositions} from 'components/ToolTip';
import userApi from 'api/user';
import ValidationGroup, {isValidPhone, isValidEmail} from 'helpers/validation';
import {balanceColumns} from 'helpers/fieldHelpers';
import {countries, dealerAdminTourFormat, userLevels} from 'helpers/constants';
import allStates from 'helpers/provinces';

class Dealership extends React.Component {
  static propTypes = {
    intl: intlShape.isRequired,
  };

  state = {
    branches: [],
    dealer: new Dealer({}),
    dealerBackup: {},
    store: {},
    addingStore: false,
    editMode: false,
    testStations: null,
  };

  componentWillMount() {
    this.group = new ValidationGroup(this._save);
  }

  componentDidMount() {
    let dealerId = session.getUser().dealerId;

    Promise.all([
      dealerApi.getDealer(dealerId),
      branchApi.getFullBranches(),
      userApi.getUsers(),
    ]).then(([dealer, branches, users]) => {
      this.setState({
        branches: branches,
        dealer: dealer,
        dealerBackup: null,
        testStations: users.reduce((total, u) => total + (u.isTestStation ? 1 : 0), 0),
      });
    });

    if (hopscotch.getState() === 'dealerAdmin:3') {
      hopscotch.startTour(dealerAdminTourFormat(this.props.intl.formatMessage), 3);
    }

    if (hopscotch.getState() === 'dealerAdmin:5') {
      hopscotch.startTour(dealerAdminTourFormat(this.props.intl.formatMessage), 5);
    }
  }

  componentWillUnmount() {
    if (hopscotch.isActive && window.location.hash !== '#customers' && window.location.hash !== '#settings/store') {
      hopscotch.endTour();
    }
  }

  onCancelEdit = () => {
    this.setState({
      editMode: false,
      dealer: this.state.dealerBackup,
      dealerBackup: null,
    });
  };

  onEdit = (e) => {
    if (e) {
      e.preventDefault();
    }

    if (session.getUser().privileges.level < userLevels.dealerAdmin) {
      return;
    }

    this.setState({
      editMode: true,
      dealerBackup: this.state.dealer.clone(),
    });
  };

  getDealerDeletionSettings = () => {
    let {formatMessage} = this.props.intl;

    return <div>
      <h2 className='with-keyline'><FormattedMessage id='preferences.account.system.deletion'/></h2>
      <div className='row'>
        <div className='col-md-6'>
          <div className='form-group'>
            <div className='row'>
              <div className='col-sm-11' style={{paddingLeft: '10px', paddingRight: '0px'}}>
                <div className='form-group'>
                  <h4><FormattedMessage id='general.manager'/></h4>
                  <hr/>
                </div>
              </div>
            </div>
            <LayoutCheckbox editMode={this.state.editMode}
                            label={formatMessage({id: 'preferences.can.delete.accounts'})}
                            dataKey='managerDeleteCustomers' key='managerDeleteCustomers'
                            dataChanged={this.dataChange} value={this.state.dealer.managerDeleteCustomers}/>
            <LayoutCheckbox editMode={this.state.editMode}
                            label={formatMessage({id: 'preferences.can.delete.systems'})} dataKey='managerDeleteSystems'
                            key='managerDeleteSystems'
                            dataChanged={this.dataChange} value={this.state.dealer.managerDeleteSystems}
                            disabled={this.state.dealer.managerDeleteCustomers}/>
          </div>
        </div>
        <div className='col-md-6'>
          <div className='form-group'>
            <div className='row'>
              <div className='col-sm-11' style={{paddingLeft: '10px', paddingRight: '0px'}}>
                <div className='form-group'>
                  <h4><FormattedMessage id='general.water.tester'/></h4>
                  <hr/>
                </div>
              </div>
            </div>
            <LayoutCheckbox editMode={this.state.editMode}
                            label={formatMessage({id: 'preferences.can.delete.accounts'})}
                            dataKey='testerDeleteCustomers' key='testerDeleteCustomers'
                            dataChanged={this.dataChange} value={this.state.dealer.testerDeleteCustomers}/>
            <LayoutCheckbox editMode={this.state.editMode}
                            label={formatMessage({id: 'preferences.can.delete.systems'})} dataKey='testerDeleteSystems'
                            key='testerDeleteSystems'
                            dataChanged={this.dataChange} value={this.state.dealer.testerDeleteSystems}
                            disabled={this.state.dealer.testerDeleteCustomers}/>
          </div>
        </div>
      </div>
    </div>;
  };

  _save = () => {
    // Upload branch logo if there is a change
    let b64data = this.logo.getImage();

    dealerApi.saveDealer(this.state.dealer, b64data)
      .then(() => {
        if (this.logo && this.logo.clear) {
          this.logo.clear();
        }
        this.setState({
          editMode: false,
          dealerBackup: null,
        });
      })
      .catch((e) => events.publish('error.user', {message: e.message}));
  };

  dataChange = (event) => {
    let dealer = this.state.dealer;

    dealer[event.key] = event.value;

    if (event.key === 'managerDeleteCustomers' && event.value === true) {
      dealer.managerDeleteSystems = true;
    }

    if (event.key === 'testerDeleteCustomers' && event.value === true) {
      dealer.testerDeleteSystems = true;
    }

    this.setState({dealer: dealer});
  };

  render() {
    const edit = this.state.editMode;
    const dealer = this.state.dealer;
    let {formatMessage} = this.props.intl;

    let footer = '';

    if (session.getUser().privileges.level >= userLevels.dealerAdmin) {
      footer = <PageFooter>
        <div style={{float: 'right'}}>
          <FooterEditButton
            editMode={this.state.editMode}
            onEdit={this.onEdit}
            onCancel={this.onCancelEdit}
            onSave={this.group.validate}/>
        </div>
      </PageFooter>;
    }

    const country = dealer.country || 'US';
    const validStates = allStates.filter(x => x.country === country);
    const shortStates = validStates.map(x => x.short);
    const nameStates = validStates.map(x => x.name);

    let dealerDeletionSettings = session.getUser().privileges.level === userLevels.dealerAdmin ? this.getDealerDeletionSettings() : '';

    let detailsFields = [];

    detailsFields.push(<LayoutInput label={formatMessage({id: 'general.administrator'})} dataKey='ownerName'
                                    dataChanged={this.dataChange} editMode={edit} value={dealer.ownerName}
                                    required={true} group={this.group}/>);
    detailsFields.push(<LayoutInput label={formatMessage({id: 'preferences.administrator.phone'})} dataKey='ownerPhone'
                                    dataChanged={this.dataChange} editMode={edit} value={dealer.ownerPhone}
                                    required={true} group={this.group} format={phoneFormat} validate={isValidPhone}
                                    validateMessage={<FormattedMessage id='general.phone.validation'/>}/>);
    detailsFields.push(<LayoutInput label={formatMessage({id: 'preferences.administrator.email'})} dataKey='ownerEmail'
                                    dataChanged={this.dataChange} editMode={edit} value={dealer.ownerEmail}
                                    required={true} group={this.group} validate={isValidEmail}
                                    validateMessage={<FormattedMessage id='general.email.validation'/>}/>);
    detailsFields.push(<LayoutSelect label={formatMessage({id: dealer.country === countries.US ? 'preferences.company.state' : 'general.province'})}
                                     dataKey='state'
                                     dataChanged={this.dataChange} editMode={edit} value={dealer.state}
                                     options={nameStates} keys={shortStates} required={true}/>);
    detailsFields.push(<LayoutInput label={formatMessage({id: 'preferences.dsm'})} editMode={false}
                                    value={dealer.dsmName}/>);

    let customerFields = [];

    customerFields.push(<ImageUpload edit={edit} image={dealer.logoLink} ref={r => this.logo = r}
                                     label={formatMessage({id: 'preferences.dealer.logo'})}
                                     description={formatMessage({id: 'preferences.dealer.logo.description'})}
                                     setEdit={this.onEdit}/>);
    customerFields.push(<LayoutInput label={formatMessage({id: 'preferences.dealer.name'})} dataKey='name'
                                     dataChanged={this.dataChange} editMode={edit}
                                     value={dealer.name} required={true} group={this.group}/>);
    customerFields.push(<LayoutInput label={formatMessage({id: 'preferences.dealer.year.established'})}
                                     dataKey='established' dataChanged={this.dataChange} editMode={edit}
                                     value={dealer.established}
                                     required={false} group={this.group} validate={val => !val || val.length === 4}
                                     type='number'
                                     validateMessage={<FormattedMessage id='preferences.error.valid.year'/>}/>);

    return (
      <div id='page-dealership' className='settings-page'>
        <h2 className='with-keyline'><FormattedMessage id='preferences.dealer.account.details'/></h2>
        <div className='col-xs-12'>
          {balanceColumns(detailsFields, 4)}
        </div>
        {dealerDeletionSettings}
        <h2 className='with-keyline'><FormattedMessage id='preferences.dealer.facing.information'/>
          <ToolTip title={formatMessage({id: 'preferences.dealer.tooltip'})} position={tooltipPositions.leftBottom}
                   style={{float: 'right'}}/>
        </h2>
        <div className='col-xs-12'>
          {balanceColumns(customerFields, 3)}
        </div>
        <div className='clearfix'/>
        {footer}
      </div>
    );
  }
}

export default injectIntl(Dealership);
